import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Droplets, Shield, Building2, CheckCircle2, Phone, Mail,
  ChevronDown, ChevronRight, FileText, Download,
  Star, Clock, BadgeCheck, Users, MapPin, AlertTriangle,
  Beaker, Gauge, FlaskConical, HeartPulse, Activity, Server,
  Award, ExternalLink, Handshake, ClipboardCheck, Microscope,
  Wrench, ListChecks, Loader2, Recycle, Truck, Zap, Atom, RotateCw, ShoppingCart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import BackToHome from "@/components/BackToHome";
import SiteHeader from "@/components/SiteHeader";

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_LOCAL = "/images/sterilemate-logo-white.png";

// Real product imagery from sterilemate.com
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=1600&q=80",
  trittonSystem: "https://sterilemate.com/wp-content/uploads/2025/10/roz-ixc-series-tritton.webp",
  trittonLogo: "https://sterilemate.com/wp-content/uploads/2025/10/triton-ixc-logo.webp",
  monitorDisplay: "https://sterilemate.com/wp-content/uploads/2025/10/monitor.webp",
  dialysisRoom2D: "https://static.prod-images.emergentagent.com/jobs/6b00dc0f-3fbc-4d33-80da-2aa733a89314/images/1d8de3d4f2f04ad4205e799840ac73ce4d3c395a2baf43b79c99f65622ff98f2.png",
  dialysisRoom3D: "https://static.prod-images.emergentagent.com/jobs/6b00dc0f-3fbc-4d33-80da-2aa733a89314/images/764e6963813aa27e0c7c11d78d5e45de4c7bb398b1ed775c531488d65d1dd10a.png",
  sterileProcessing: "https://images.unsplash.com/photo-1586528243231-474b0f741873?w=900&q=80",
  dialysis: "https://images.unsplash.com/photo-1664902265139-934219cee42f?w=900&q=80",
  laboratory: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=900&q=80",
};

// Real public links + downloads (already wired into the demo page)
const LINKS = {
  marketingPage: "https://sterilemate.com/healthcare-water-purification/",
  commercialRO: "https://sterilemate.com/commercial-reverse-osmosis-water-purification-systems/",
  mediQAPdf: "https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/jukvtufy_MediQA_Heat_Disinfect_RO.pdf",
  heatsanPdf: "https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/ep4sumnq_Heatsan_Heat_Disinfection_System.pdf",
  postTreatmentPdf: "https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/76c4dha4_D-357-01-Post-Treatment-Rack.pdf",
  waterCapabilitiesPdf: `${process.env.REACT_APP_BACKEND_URL}/api/static/downloads/sterilemate_water_purification_2026.pdf`,
};

// ----- Service Card (matches GC layout) ---------------------------------------
const ServiceCard = ({ icon: Icon, title, description, items, color, image, testid }) => {
  const [expanded, setExpanded] = useState(false);
  const colorMap = {
    sky:     { bg: "bg-sky-50",     border: "border-sky-200",     accent: "text-sky-700",     badge: "bg-sky-100 text-sky-800",     iconBg: "bg-sky-500" },
    blue:    { bg: "bg-blue-50",    border: "border-blue-200",    accent: "text-blue-700",    badge: "bg-blue-100 text-blue-800",    iconBg: "bg-blue-500" },
    cyan:    { bg: "bg-cyan-50",    border: "border-cyan-200",    accent: "text-cyan-700",    badge: "bg-cyan-100 text-cyan-800",    iconBg: "bg-cyan-500" },
    teal:    { bg: "bg-teal-50",    border: "border-teal-200",    accent: "text-teal-700",    badge: "bg-teal-100 text-teal-800",    iconBg: "bg-teal-500" },
  };
  const cs = colorMap[color];
  return (
    <div className={`rounded-xl border ${cs.border} overflow-hidden transition-all duration-300 hover:shadow-lg`} data-testid={testid}>
      <div className="relative h-48 overflow-hidden">
        <img src={image} alt={title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className={`absolute top-4 left-4 ${cs.iconBg} p-2 rounded-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
      </div>
      <div className={`p-5 ${cs.bg}`}>
        <p className="text-sm text-slate-600 mb-4">{description}</p>
        <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 text-sm font-semibold ${cs.accent} mb-3`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {expanded ? "Hide Details" : "View All Capabilities"}
        </button>
        {expanded && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            {items.map((item, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${cs.badge}`}>
                <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ----- Page -------------------------------------------------------------------
export default function WaterTreatmentPage() {
  // Always start at the top when navigating in from the homepage tile
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // DI Tank Exchange catalog — fetched from the single source of truth
  // (backend/data/di_tank_catalog.json) so this page, the marketplace catalog,
  // and email templates can't drift out of sync.
  const [diTanks, setDiTanks] = useState([]);
  const [diStartingAt, setDiStartingAt] = useState(115);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/catalog/di-tanks`)
      .then((res) => {
        if (cancelled) return;
        setDiTanks(res.data?.products || []);
        if (res.data?.starting_at) setDiStartingAt(res.data.starting_at);
      })
      .catch(() => { /* keep defaults — page still renders meaningfully */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="water-treatment-page">
      <SiteHeader />
      {/* ============ HERO ============ */}
      <div className="relative h-[520px] overflow-hidden">
        <img src={IMAGES.hero} alt="Healthcare Water Purification" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/60 to-transparent" />
        <div className="hidden md:flex absolute top-6 right-6 items-center gap-3 z-30">
          <img src={LOGO_LOCAL} alt="Sterile Mate" className="w-14 h-12 rounded-lg object-contain" />
          <div className="hidden md:block">
            <p className="text-white font-bold text-sm">Sterile Mate LLC</p>
            <p className="text-cyan-400 text-xs">Healthcare Water Purification</p>
          </div>
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-3xl px-8 md:px-16">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Water Treatment Systems for Healthcare. <span className="text-cyan-400">AAMI Compliant.</span>
            </h1>
            <p className="text-base lg:text-lg text-slate-300 mb-6 max-w-2xl">
              From sterile processing to dialysis to research labs, Sterile Mate engineers and installs water purification systems built to AAMI ST108. We supply TRITTON IXC, MediQA, Heatsan, and Puretech commercial RO platforms — backed by Texas-licensed master plumbers and infection-control-trained technicians.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={LINKS.waterCapabilitiesPdf}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-colors shadow-lg"
                data-testid="hero-download-water-capabilities"
              >
                <Download className="w-4 h-4" /> Download Full Capabilities Brochure
              </a>
              <a
                href={LINKS.mediQAPdf}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors shadow-lg"
                data-testid="hero-download-mediqa"
              >
                <Download className="w-4 h-4" /> MediQA Spec Sheet
              </a>
              <a href="#assessment-form" className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 backdrop-blur hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20" data-testid="hero-quote-cta">
                <Mail className="w-4 h-4" /> Request a Partnership Assessment
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ============ TRUST BAR ============ */}
      <div className="bg-slate-900 border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap justify-center gap-8 md:gap-16">
          {[
            { icon: BadgeCheck, text: "AAMI ST108 Ready" },
            { icon: Shield, text: "FDA-Compliant Materials" },
            { icon: Award, text: "AAMI ST108 Specialists" },
            { icon: Users, text: "Master Plumbers on Staff" },
            { icon: Star, text: "VA + State Contractor" },
            { icon: MapPin, text: "Houston, TX" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-slate-300">
              <item.icon className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* ============ WHO WE SERVE ============ */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-cyan-600 mb-2">FROM SPD TO DIALYSIS TO RESEARCH</h2>
            <p className="text-3xl font-bold text-slate-900 mb-4">Where Hospital Water Becomes Critical Infrastructure</p>
            <p className="text-slate-500 max-w-2xl mx-auto">Sterile processing, dialysis, lab and pharmacy each require a different water spec. We design, install, validate, and maintain systems that meet AAMI ST108, ANSI/AAMI/ISO 13959, and CMS Conditions of Coverage.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { name: "Sterile Processing (CSSD)", desc: "Critical Water for the final rinse — AAMI ST108 endotoxin, conductivity, and TOC limits met out of the box.", type: "AAMI ST108", img: IMAGES.sterileProcessing },
              { name: "Hemodialysis Centers", desc: "RO + DI loops, heat-disinfection, and full ANSI/AAMI/ISO 13959 monitoring. Centralized or per-station.", type: "AAMI 13959", img: IMAGES.dialysis },
              { name: "Research & Pharmacy Labs", desc: "Type I/II/III lab water with TOC and resistivity polish stages. Compliance-ready validation reports.", type: "USP / CLSI", img: IMAGES.laboratory },
            ].map((ref, i) => (
              <div key={i} className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`water-segment-${i}`}>
                <div className="h-40 overflow-hidden">
                  <img src={ref.img} alt={ref.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-cyan-600" />
                    <span className="text-xs font-bold text-cyan-700 bg-cyan-50 px-2 py-1 rounded">{ref.type}</span>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{ref.name}</h3>
                  <p className="text-sm text-slate-500">{ref.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ TRITTON FEATURE BAND ============ */}
        <section className="py-12 -mx-6 px-6 bg-gradient-to-br from-slate-50 to-cyan-50/40 rounded-3xl">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <div>
              <img src={IMAGES.trittonLogo} alt="TRITTON IXC" className="h-10 mb-4 object-contain" />
              <h2 className="text-3xl font-bold text-slate-900 mb-3">TRITTON IXC Series Reverse Osmosis</h2>
              <p className="text-slate-600 mb-4">A flagship hospital-grade RO platform — heat-disinfectable, dual-pump redundant, and designed for centralized hospital deployments. Compatible with our AskIsaac telemetry layer for live TDS, conductivity, and pump-pressure monitoring.</p>
              <ul className="space-y-2 mb-6 text-sm text-slate-700">
                {[
                  "AAMI ST108 Critical Water output",
                  "Heat disinfection cycle (no chemicals)",
                  "Dual-pump fail-over for 24/7 hospital uptime",
                  "Touchscreen monitor with full event logging",
                  "Remote AskIsaac dashboard integration",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <a href={LINKS.commercialRO} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-lg" data-testid="tritton-commercial-link">
                  <ExternalLink className="w-4 h-4" /> Product Brochure
                </a>
                <a href={LINKS.mediQAPdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-300 text-cyan-700 hover:bg-cyan-50 text-sm font-semibold rounded-lg" data-testid="mediqa-spec-link">
                  <FileText className="w-4 h-4" /> MediQA Spec
                </a>
                <a href="#di-tank-exchange" className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-300 text-cyan-700 hover:bg-cyan-50 text-sm font-semibold rounded-lg" data-testid="jump-to-di-exchange">
                  <Truck className="w-4 h-4" /> DI Tank Exchange
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img src={IMAGES.trittonSystem} alt="TRITTON IXC RO System" className="col-span-2 w-full rounded-xl shadow-md object-cover" />
              <img src={IMAGES.monitorDisplay} alt="Touchscreen monitor" className="w-full rounded-xl shadow-sm object-cover" />
              <img src={IMAGES.dialysisRoom3D} alt="Dialysis room rendering" className="w-full rounded-xl shadow-sm object-cover" />
            </div>
          </div>
        </section>

        {/* ============ CORE CAPABILITIES ============ */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-slate-500 mb-2">CORE CAPABILITIES</h2>
            <p className="text-3xl font-bold text-slate-900">Four Disciplines. One Vendor.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <ServiceCard
              icon={Droplets}
              title="Reverse Osmosis & Pretreatment"
              description="Single-pass, double-pass, and heat-disinfectable RO systems sized for hospital flow and quality demands — including softening, carbon, and ion-exchange polish stages."
              image={IMAGES.trittonSystem}
              color="sky"
              testid="capability-ro"
              items={[
                "TRITTON IXC RO Systems", "MediQA Heat-Disinfect RO", "Heatsan Heat Loops",
                "Multimedia Filtration", "Carbon Filtration", "Water Softeners",
                "Mixed-Bed Polishers", "DI Tank Service", "Ultrafiltration", "UV Disinfection",
              ]}
            />
            <ServiceCard
              icon={HeartPulse}
              title="Dialysis Water Systems"
              description="Centralized RO + heat-disinfected loop design, per-station drops, and full ANSI/AAMI/ISO 13959 compliance — engineered for hemodialysis facility licensure."
              image={IMAGES.dialysis}
              color="blue"
              testid="capability-dialysis"
              items={[
                "Central RO Design", "Heat-Disinfect Loops", "Per-Station Drops",
                "Concentrate Mixing", "AAMI 13959 Validation", "Loop Sanitization",
                "Backflow Prevention", "Bicarb Lines", "Patient-Care Polishing", "Annual Re-Validation",
              ]}
            />
            <ServiceCard
              icon={FlaskConical}
              title="Sterile Processing & Lab Water"
              description="Critical Water for SPD final rinse meeting AAMI ST108. Type I/II/III lab water with resistivity, TOC, and bacterial polish for research, pharmacy, and clinical labs."
              image={IMAGES.sterileProcessing}
              color="cyan"
              testid="capability-spd"
              items={[
                "AAMI ST108 Critical Water", "USP Lab Water", "TOC Polishers",
                "Resistivity Loops", "Pharmacy USP <797>", "Clinical CLSI",
                "Validation Reports", "Endotoxin Testing", "Sample Port Install", "Quarterly Service",
              ]}
            />
            <ServiceCard
              icon={Server}
              title="Service, Validation & Compliance"
              description="Preventive maintenance, AAMI annual re-validation, water-quality reporting, and 24/7 emergency response — bundled into a single AskIsaac service contract."
              image={IMAGES.laboratory}
              color="teal"
              testid="capability-service"
              items={[
                "AAMI Annual Re-Validation", "Quarterly PM", "Water Quality Reports",
                "Filter Change Programs", "Membrane Replacements", "24/7 Emergency Response",
                "Compliance Documentation", "Joint Commission Audits", "Loop Sanitization Logs", "AskIsaac Telemetry",
              ]}
            />
          </div>
        </section>

        {/* ============ COMPLIANCE STORY ============ */}
        <section className="py-16 -mx-6 px-6 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-base lg:text-lg font-bold text-rose-600 mb-2">AAMI ST108 IS HERE</h2>
                <p className="text-3xl font-bold text-slate-900 mb-6">Why Most Hospitals Fail Their First ST108 Audit</p>
                <p className="text-slate-600 mb-6">AAMI ST108 supersedes ST79 Annex M and dramatically tightens the water-quality bar for every step of instrument reprocessing. It changes the spec for utility, critical, and steam — and most facilities won&apos;t meet it without a system upgrade.</p>
                <div className="space-y-3">
                  {[
                    { label: "Conductivity ≤ 10 µS/cm", desc: "Critical Water final-rinse standard for SPD" },
                    { label: "Endotoxin ≤ 10 EU/mL", desc: "New bacterial endotoxin ceiling for instrument water" },
                    { label: "TOC ≤ 1 mg/L", desc: "Total organic carbon limit for the final rinse" },
                    { label: "Heavy Metal Limits", desc: "Lead, copper, iron limits documented per audit" },
                    { label: "Documented Validation", desc: "Annual independent validation report required" },
                    { label: "Sampling Program", desc: "Defined sample ports, schedule, and records" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3 border border-slate-200">
                      <CheckCircle2 className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-sm text-slate-900">{item.label}</span>
                        <p className="text-xs text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img src={IMAGES.dialysisRoom2D} alt="Dialysis room AutoCAD" className="rounded-2xl shadow-xl" />
                <div className="absolute -bottom-4 -right-4 bg-rose-500 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  <div>
                    <p className="font-bold text-sm">Effective Now</p>
                    <p className="text-xs opacity-90">AAMI ST108:2023</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SERVICE PROCESS ============ */}
        <section className="py-16">
          <div className="bg-slate-900 -mx-6 px-6 py-16 rounded-3xl">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-base lg:text-lg font-bold text-cyan-400 mb-2">FROM SURVEY TO START-UP</h2>
                <p className="text-3xl font-bold text-white">Five Stages, Documented at Every Step</p>
              </div>
              <div className="grid md:grid-cols-5 gap-6">
                {[
                  { n: "01", t: "Site Survey", d: "Feed-water analysis, demand modeling, AAMI gap audit, mechanical-room layout review." },
                  { n: "02", t: "System Design", d: "Specification with single-line P&ID, redundancy plan, and AAMI/ANSI compliance map." },
                  { n: "03", t: "Install & Plumbing", d: "Texas-licensed master plumbers, ICRA barriers, and infection-control-trained installers." },
                  { n: "04", t: "Validation", d: "Endotoxin, conductivity, TOC, and microbial sampling. Independent third-party validation report." },
                  { n: "05", t: "Service Plan", d: "Quarterly PM, annual re-validation, AskIsaac telemetry, 24/7 emergency response." },
                ].map((step) => (
                  <div key={step.n} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center mx-auto mb-4">
                      <span className="text-lg font-bold text-white">{step.n}</span>
                    </div>
                    <h4 className="font-bold text-white mb-2">{step.t}</h4>
                    <p className="text-sm text-slate-400">{step.d}</p>
                  </div>
                ))}
              </div>

              {/* AskIsaac Section */}
              <div className="mt-16 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <img src={LOGO_LOCAL} alt="Sterile Mate" className="w-12 h-10 rounded-lg object-contain" />
                      <div>
                        <p className="text-cyan-400 text-xs font-bold tracking-widest">POWERED BY</p>
                        <p className="text-white text-xl font-bold">AskIsaac — Live Water Telemetry</p>
                      </div>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed mb-6">
                      Every Sterile Mate water system streams live TDS, conductivity, pump pressure, and resistivity into <span className="text-cyan-400 font-semibold">AskIsaac</span> — our intelligent operations platform. When a parameter drifts, AskIsaac dispatches a Texas-licensed technician before the dialysis floor or SPD has a documented event.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { t: "Live TDS / Conductivity", d: "Critical Water parameters streamed from every loop" },
                        { t: "Auto-Validation Logs", d: "AAMI/ANSI sampling records, audit-ready" },
                        { t: "Predictive Service", d: "Membrane life + filter cycles forecast in advance" },
                        { t: "24/7 Dispatch", d: "Telegram-first emergency response, no app required" },
                      ].map((feat, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-cyan-400 text-xs font-bold mb-1">{feat.t}</p>
                          <p className="text-slate-400 text-xs">{feat.d}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ DI TANK EXCHANGE PROGRAM ============ */}
        <section id="di-tank-exchange" className="py-16 scroll-mt-20" data-testid="di-tank-program-section">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-cyan-600 mb-2">DI TANK EXCHANGE PROGRAM</h2>
            <p className="text-3xl font-bold text-slate-900 mb-4">Deionized Water on Demand &mdash; Without the Hassle.</p>
            <p className="text-slate-500 max-w-3xl mx-auto">
              Sterile Mate&rsquo;s Portable DI Tank Exchange Program is a cost-effective, compliant, and reliable solution for the healthcare industry. We deliver a consistent supply of high-purity deionized water for <strong>sterile processing, endoscope reprocessing, dialysis, and laboratory support</strong> &mdash; built to meet <strong>AAMI ST108</strong> water-quality standards.
            </p>
          </div>

          {/* Four-pillar value strip */}
          <div className="grid md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: Truck, t: "Next-Day Exchange", d: "Dispatch coordinates next-business-day swaps. Same-day available for emergencies.", testid: "di-pillar-next-day" },
              { icon: Recycle, t: "No On-Site Chemicals", d: "No on-site chemical handling, no maintenance, no wastewater discharge.", testid: "di-pillar-no-chem" },
              { icon: Shield, t: "cGMP / ISO 9001 Resin", d: "Premium-grade resins regenerated under strict QC, tested at multiple points.", testid: "di-pillar-cgmp" },
              { icon: Gauge, t: "Remote Monitoring", d: "Real-time visibility + proactive alerts before tank exhaustion stops your line.", testid: "di-pillar-remote" },
            ].map((p) => (
              <div key={p.t} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-cyan-400 hover:shadow-md transition-all" data-testid={p.testid}>
                <div className="w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center mb-3">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-slate-900 text-sm mb-1.5">{p.t}</h3>
                <p className="text-sm text-slate-600 leading-snug">{p.d}</p>
              </div>
            ))}
          </div>

          {/* Tank technology overview */}
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            <div className="bg-gradient-to-br from-cyan-50 to-sky-50 border border-cyan-200 rounded-xl p-6" data-testid="di-tech-mixed-bed">
              <div className="flex items-center gap-2 mb-3">
                <Atom className="w-5 h-5 text-cyan-700" />
                <span className="text-xs font-bold tracking-widest text-cyan-700 uppercase">Mixed Bed</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Ultra-Pure Polish &mdash; up to 18.2 M&Omega;</h3>
              <p className="text-sm text-slate-600 mb-3">
                Strong-acid cation + strong-base anion resins combined in a single tank deliver exceptionally high purity at a stable, near-neutral pH &mdash; the highest achievable water-purification standard for critical healthcare and life-science applications.
              </p>
              <div className="text-xs text-slate-500 space-y-1 pt-3 border-t border-cyan-200">
                <div className="flex justify-between"><span>Resistivity</span><strong className="text-slate-700">&le; 18.2 M&Omega;</strong></div>
                <div className="flex justify-between"><span>Conductivity</span><strong className="text-slate-700">&ge; 0.056 &micro;S</strong></div>
                <div className="flex justify-between"><span>pH</span><strong className="text-slate-700">Neutral</strong></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-xl p-6" data-testid="di-tech-dual-bed">
              <div className="flex items-center gap-2 mb-3">
                <RotateCw className="w-5 h-5 text-sky-700" />
                <span className="text-xs font-bold tracking-widest text-sky-700 uppercase">Dual Bed</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">High-Performance Two-Stage</h3>
              <p className="text-sm text-slate-600 mb-3">
                Two-stage process: cation tank removes Ca, Mg, Fe, Na, Mn; anion tank removes chloride, sulfates, nitrates, CO&#8322;. Pair with a mixed bed downstream for ultra-pure polish.
              </p>
              <div className="text-xs text-slate-500 space-y-1 pt-3 border-t border-sky-200">
                <div className="flex justify-between"><span>Resistivity</span><strong className="text-slate-700">&le; 200K &Omega;</strong></div>
                <div className="flex justify-between"><span>TDS</span><strong className="text-slate-700">~ 2.5 ppm</strong></div>
                <div className="flex justify-between"><span>pH</span><strong className="text-slate-700">&gt; 7 (strong base)</strong></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-6" data-testid="di-tech-gac">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-slate-700" />
                <span className="text-xs font-bold tracking-widest text-slate-700 uppercase">GAC Pre-Treatment</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Protects Downstream DI &amp; RO</h3>
              <p className="text-sm text-slate-600 mb-3">
                Granular activated carbon removes chlorine, chloramines, and dissolved organics &mdash; preventing oxidation damage and organic fouling on your DI and RO stages downstream.
              </p>
              <div className="text-xs text-slate-500 space-y-1 pt-3 border-t border-slate-200">
                <div className="flex justify-between"><span>Removes</span><strong className="text-slate-700">Cl, NH&#8322;Cl, organics</strong></div>
                <div className="flex justify-between"><span>Stage</span><strong className="text-slate-700">Pre-DI / Pre-RO</strong></div>
                <div className="flex justify-between"><span>Extends life</span><strong className="text-slate-700">DI + RO resin</strong></div>
              </div>
            </div>
          </div>

          {/* Pricing strip — three tank sizes with email-campaign images & prices */}
          <div className="mb-10" data-testid="di-pricing-strip">
            <div className="text-center mb-6">
              <p className="text-xs font-bold tracking-widest text-cyan-700 uppercase mb-1">Transparent Pricing</p>
              <h3 className="text-2xl font-bold text-slate-900">Three tank sizes. Starting at <span className="text-cyan-600">${Math.floor(diStartingAt)}</span>/tank.</h3>
              <p className="text-sm text-slate-500 mt-1">Per-tank exchange pricing &middot; fuel surcharge per service trip &middot; one-time setup fee for new accounts.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {diTanks.map((t) => (
                <div key={t.erp} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-cyan-400 hover:shadow-md transition-all" data-testid={`di-price-card-${t.erp}`}>
                  <div className="relative h-40 overflow-hidden bg-slate-100">
                    <img src={`${API}/api/static/backorders/${t.image_filename}`} alt={`${t.size_label} DI Tank — ${t.capacity}`} className="w-full h-full object-cover" loading="lazy" />
                    <span className={`absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shadow ${t.badge_color_class || "bg-cyan-500 text-white"}`}>{t.badge}</span>
                  </div>
                  <div className="p-4">
                    <p className="text-[10px] font-bold tracking-widest text-cyan-700 uppercase">{t.size_label} DI Tank</p>
                    <p className="text-lg font-bold text-slate-900 mt-0.5">{t.capacity}</p>
                    <p className="text-xs text-slate-500 mt-1 mb-3">{t.best_for_short || t.best_for}</p>
                    <div className="flex items-baseline justify-between border-t border-slate-100 pt-3">
                      <div>
                        <span className="text-2xl font-bold text-slate-900">${Number(t.unit_price).toFixed(2)}</span>
                        <span className="text-xs text-slate-500 ml-1">/ tank exchange</span>
                      </div>
                      <Link
                        to="/marketplace/di-tank-exchange"
                        className="text-xs font-bold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1"
                        data-testid={`di-price-cta-${t.erp}`}
                      >
                        Add to Cart <ChevronRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 text-center mt-3">
              All prices are subject to change. Cancel anytime — no long-term contracts. Volume discounts available — <a href="tel:2814563458" className="text-cyan-700 font-semibold">call 281.456.3458</a> for facility-wide pricing.
            </p>
          </div>

          {/* Compliance + critical-environment band */}
          <div className="bg-slate-900 -mx-6 px-6 py-10 rounded-3xl mb-8">
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs font-bold tracking-widest text-cyan-400 uppercase mb-2">High-Purity Assurance</p>
                <h3 className="text-2xl font-bold text-white mb-3">Regenerated to cGMP &middot; ISO 9001 &middot; AAMI ST108-Ready.</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  All deionization resins are regenerated under strict QC aligned with current Good Manufacturing Practices and ISO 9001 protocols. We use only premium-grade ion-exchange resins, tested at multiple points during regeneration for optimal effluent quality and throughput consistency.
                </p>
                <p className="text-slate-300 text-sm leading-relaxed">
                  For <strong className="text-white">critical healthcare, lab, and pharmaceutical environments</strong> that require compliance with <strong className="text-white">USP, ASTM, CLSI, ISO 3696 (Types I&ndash;IV), CAP, and Milli-Q&reg; specifications</strong>, we offer dedicated, isolated DI tank service options with full documentation and traceability.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {["USP / CLSI", "ISO 3696 I-IV", "AAMI ST108", "CAP", "Milli-Q®", "cGMP / ISO 9001"].map((badge) => (
                  <div key={badge} className="bg-white/5 border border-white/10 rounded-lg px-3 py-3 text-center" data-testid={`di-compliance-${badge.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                    <BadgeCheck className="w-5 h-5 text-cyan-400 mx-auto mb-1.5" />
                    <p className="text-white text-xs font-semibold">{badge}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order from the marketplace CTA */}
          <div className="bg-gradient-to-r from-cyan-600 to-sky-700 rounded-2xl px-6 py-8 mb-12 text-white" data-testid="di-marketplace-cta">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center max-w-6xl mx-auto">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-cyan-100 mb-1">Order in minutes — no contracts</p>
                <h3 className="text-2xl font-bold mb-1.5">Add tanks to your cart and check out with a PO.</h3>
                <p className="text-cyan-50 text-sm">
                  Three sizes &middot; starting at <strong>${Math.floor(diStartingAt)}/tank</strong> &middot; next-day delivery (same-day available for emergencies). The full pricing menu lives in our marketplace.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Link
                  to="/marketplace/di-tank-exchange"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white text-cyan-700 font-bold text-sm hover:bg-cyan-50 transition-colors whitespace-nowrap"
                  data-testid="di-marketplace-link"
                >
                  <ShoppingCart className="w-4 h-4" /> Browse Marketplace
                </Link>
                <a
                  href="#di-tank-exchange-form-anchor"
                  onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="di-tank-exchange-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-cyan-900/40 border border-white/30 text-white font-bold text-sm hover:bg-cyan-900/60 transition-colors whitespace-nowrap"
                  data-testid="di-jump-to-form"
                >
                  <Truck className="w-4 h-4" /> Or Request a One-Off Swap
                </a>
              </div>
            </div>
          </div>

          {/* Exchange request form */}
          <DITankExchangeForm />
        </section>

        {/* ============ DOWNLOADS ============ */}
        <section className="py-12">
          <div className="text-center mb-8">
            <h2 className="text-base lg:text-lg font-bold text-cyan-600 mb-2">SPEC SHEETS & DOWNLOADS</h2>
            <p className="text-3xl font-bold text-slate-900">Engineer-Ready Documentation</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: "Sterile Mate Water Capabilities Brochure (2026)", d: "Full-line brochure \u2014 licensed technicians, GC/electrical/plumbing, RO, DI exchange, softeners.", href: LINKS.waterCapabilitiesPdf, icon: Beaker, testid: "download-water-capabilities" },
              { t: "MediQA Heat-Disinfect RO", d: "Full system spec, performance curves, install requirements.", href: LINKS.mediQAPdf, icon: Beaker, testid: "download-mediqa" },
              { t: "Heatsan Heat Disinfection", d: "Loop disinfection module \u2014 chemical-free dialysis loop sanitization.", href: LINKS.heatsanPdf, icon: Gauge, testid: "download-heatsan" },
              { t: "Post-Treatment Rack D-357", d: "Polish skid for downstream UV, UF, and resistivity stages.", href: LINKS.postTreatmentPdf, icon: Activity, testid: "download-posttreatment" },
            ].map((d) => (
              <a
                key={d.t}
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white border border-slate-200 rounded-xl p-5 hover:border-cyan-300 hover:shadow-md transition-all"
                data-testid={d.testid}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center">
                    <d.icon className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-bold text-cyan-600 uppercase tracking-wider">PDF · 2 MB</span>
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{d.t}</h3>
                <p className="text-sm text-slate-500 mb-3">{d.d}</p>
                <span className="inline-flex items-center gap-1 text-cyan-600 text-sm font-semibold group-hover:gap-2 transition-all">
                  Download <Download className="w-3.5 h-3.5" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* ============ PARTNERSHIP APPROACH ============ */}
        <section className="py-16" data-testid="partnership-approach-section">
          <div className="text-center mb-12">
            <h2 className="text-base lg:text-lg font-bold text-cyan-600 mb-2">OUR PARTNERSHIP APPROACH</h2>
            <p className="text-3xl font-bold text-slate-900 mb-4">More Than a Vendor &mdash; a Compliance Partner.</p>
            <p className="text-slate-500 max-w-3xl mx-auto">
              We do not just sell water-treatment systems and ship you replacement filters on a calendar. We partner with your facility, audit your current water program, and build you a roadmap to stay <strong>AAMI ST108</strong> and <strong>Joint Commission</strong> ready &mdash; every survey, every year.
            </p>
          </div>

          {/* 4-step partnership flow */}
          <div className="grid md:grid-cols-4 gap-5 mb-12">
            {[
              { n: "01", icon: Handshake, title: "Partner & Discover", desc: "We meet your SPD, Dialysis, Bio-Med, and Facilities leads. We listen first — your pain points, prior survey findings, and what success looks like for your patients." },
              { n: "02", icon: ClipboardCheck, title: "AAMI ST108 Compliance Gap", desc: "We benchmark your current water program against AAMI ST108, ANSI/AAMI/ISO 13959, USP/CLSI, and CMS Conditions of Coverage — line by line." },
              { n: "03", icon: Microscope, title: "On-Site In-Service Assessment", desc: "An engineer + master plumber walk the building, pull live samples, examine your RO/DI/loop system, and review the last 12 months of water-quality reports with you." },
              { n: "04", icon: ListChecks, title: "Roadmap & Service Plan", desc: "We deliver a written gap report, an upgrade/replace recommendation, a monitoring plan, and a transparent service-plan quote — never a black-box subscription." },
            ].map((s) => (
              <div key={s.n} className="relative bg-white border border-slate-200 rounded-xl p-5 hover:border-cyan-400 hover:shadow-md transition-all" data-testid={`partnership-step-${s.n}`}>
                <span className="absolute -top-3 left-5 text-[10px] font-bold tracking-widest text-white bg-cyan-600 px-2 py-1 rounded-md">STEP {s.n}</span>
                <s.icon className="w-7 h-7 text-cyan-600 mb-3 mt-3" />
                <h3 className="font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* What to expect on the in-service visit */}
          <div className="bg-gradient-to-br from-cyan-50 to-sky-50 border border-cyan-200 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-cyan-600 text-white flex items-center justify-center">
                <Microscope className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-cyan-700 font-bold">Step 03 &mdash; In-Service Visit</p>
                <h3 className="text-2xl font-bold text-slate-900">What to expect on the in-service assessment.</h3>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-3">
              {[
                { icon: FileText, t: "Water-quality reports review", d: "We sit with your bio-med / facilities team and walk through the last 12 months of your RO, DI, post-mix, endotoxin, and TOC reports." },
                { icon: Beaker, t: "Live water-sample testing on-site", d: "We pull samples at the feed, post-RO, post-DI, and post-mix points and test conductivity, TDS, hardness, free chlorine, and pH in front of you." },
                { icon: Gauge, t: "RO / DI loop examination", d: "Visual + instrumented inspection of your skid, pressure tanks, blend valves, holding tanks, and distribution loop — including dead legs that fail at survey." },
                { icon: Activity, t: "Monitoring & alarm audit", d: "We confirm whether you actually have continuous monitoring at post-RO, post-DI, and post-mix — and whether thresholds + alerting are AAMI-defensible." },
                { icon: Wrench, t: "Current system condition score", d: "We give your existing equipment a 1\u20135 condition score per stage with photographic evidence — useful for capital budget conversations." },
                { icon: Shield, t: "AAMI ST108 + Joint Commission gap report", d: "Within 5 business days you receive a written gap report, line-mapped to ST108 / 13959 / CMS so you can hand it to your survey-readiness committee." },
              ].map((it, i) => (
                <div key={i} className="flex gap-3" data-testid={`in-service-bullet-${i}`}>
                  <it.icon className="w-5 h-5 text-cyan-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">{it.t}</p>
                    <p className="text-sm text-slate-600 leading-snug">{it.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-cyan-200">
              <p className="text-sm text-slate-700"><strong>No-cost initial visit.</strong> We only quote work after we have walked your building.</p>
              <a href="#assessment-form" className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold rounded-lg" data-testid="in-service-cta">
                Request your assessment <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* ============ ASSESSMENT REQUEST FORM ============ */}
        <section id="assessment-form" className="py-16 scroll-mt-20">
          <WaterAssessmentForm />
        </section>

        {/* ============ CTA ============ */}
        <section className="py-16">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 rounded-2xl p-10 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready for an AAMI ST108 Site Survey?</h2>
            <p className="text-slate-300 max-w-2xl mx-auto mb-8">A Sterile Mate engineer will walk your CSSD, dialysis, or lab spaces, take feed-water samples, and return a documented gap report against ST108 / 13959 within 5 business days.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:george.broughton@sterilemate.com?subject=Water%20Treatment%20Site%20Survey"
                className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors"
                data-testid="cta-email"
              >
                <Mail className="w-5 h-5" /> george.broughton@sterilemate.com
              </a>
              <a href="tel:2814563458" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20" data-testid="cta-phone-1">
                <Phone className="w-5 h-5" /> 281.456.3458
              </a>
              <Link to="/demo/water-purification" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors border border-white/20" data-testid="cta-demo-deck">
                <FileText className="w-5 h-5" /> View Full Slide Deck
              </Link>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <a href={LINKS.marketingPage} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">www.sterilemate.com/healthcare-water-purification</a>
              <a href="https://sterilemate.com" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 text-sm font-medium">AskIsaac.io</a>
            </div>
            <p className="text-slate-400 text-sm mt-4">AAMI ST108 Specialists | Texas-Licensed Master Plumbers | Houston, TX</p>
          </div>
        </section>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// WaterAssessmentForm — public form on /solutions/water-treatment
// Submits to POST /api/water-assessment/request
// -----------------------------------------------------------------------------
const DEPARTMENTS = [
  "Sterile Processing (SPD/CSSD)",
  "Hemodialysis",
  "Biomedical Engineering",
  "Facilities / Engineering",
  "Infection Prevention",
  "Pharmacy / IV Compounding",
  "Research / Clinical Lab",
  "Administration",
  "Other",
];

const COMPLIANCE_GOALS = [
  "AAMI ST108 (sterile processing critical water)",
  "ANSI/AAMI/ISO 13959 (dialysis water)",
  "Joint Commission survey readiness",
  "CMS Conditions of Coverage (ESRD)",
  "USP / CLSI lab water",
  "Capital planning / system replacement",
  "Service contract review",
];

function WaterAssessmentForm() {
  const [form, setForm] = useState({
    hospital_name: "",
    first_name: "",
    last_name: "",
    title: "",
    email: "",
    phone: "",
    department: "",
    current_system: "",
    comments: "",
  });
  const [goals, setGoals] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGoal = (g) =>
    setGoals((curr) => (curr.includes(g) ? curr.filter((x) => x !== g) : [...curr, g]));

  const submit = async (e) => {
    e.preventDefault();
    // Minimal client-side validation
    const required = ["hospital_name", "first_name", "last_name", "email", "department"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill in ${k.replace("_", " ")}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/water-assessment/request`, {
        ...form,
        compliance_goals: goals,
      });
      setSubmitted(true);
      toast.success("Assessment request sent. Our team will reach out within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Submission failed. Please try again or call 281.456.3458.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center" data-testid="assessment-form-success">
        <div className="w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank you &mdash; your request is in.</h2>
        <p className="text-slate-600 max-w-xl mx-auto">
          A Sterile Mate Water Treatment specialist will reach out within <strong>one business day</strong> to schedule your in-service AAMI ST108 partnership assessment. We have also emailed you a copy with the visit checklist.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="water-assessment-form">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 px-8 lg:px-10 py-7 text-white">
        <p className="text-xs uppercase tracking-widest text-cyan-300 font-bold mb-1">Request your in-service assessment</p>
        <h2 className="text-2xl lg:text-3xl font-bold mb-1.5">Let&rsquo;s start the AAMI ST108 partnership conversation.</h2>
        <p className="text-slate-300 text-sm max-w-2xl">
          Tell us a few things about your facility and current water program. A Sterile Mate engineer will follow up within one business day to schedule the on-site visit &mdash; <strong className="text-white">no cost, no obligation</strong>.
        </p>
      </div>

      <form onSubmit={submit} className="px-8 lg:px-10 py-8 grid sm:grid-cols-2 gap-x-6 gap-y-5">
        <div className="sm:col-span-2">
          <Label htmlFor="hospital_name" className="text-xs font-bold uppercase tracking-wider text-slate-600">Hospital / Facility name *</Label>
          <Input id="hospital_name" value={form.hospital_name} onChange={(e) => set("hospital_name", e.target.value)} placeholder="e.g. Memorial Hermann Northeast Hospital" data-testid="form-hospital-name" required />
        </div>
        <div>
          <Label htmlFor="first_name" className="text-xs font-bold uppercase tracking-wider text-slate-600">First name *</Label>
          <Input id="first_name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Alice" data-testid="form-first-name" required />
        </div>
        <div>
          <Label htmlFor="last_name" className="text-xs font-bold uppercase tracking-wider text-slate-600">Last name *</Label>
          <Input id="last_name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Smith" data-testid="form-last-name" required />
        </div>
        <div>
          <Label htmlFor="title" className="text-xs font-bold uppercase tracking-wider text-slate-600">Title / Role</Label>
          <Input id="title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="SPD Manager, Director of Facilities, Bio-Med Tech..." data-testid="form-title" />
        </div>
        <div>
          <Label htmlFor="department" className="text-xs font-bold uppercase tracking-wider text-slate-600">Department *</Label>
          <Select value={form.department} onValueChange={(v) => set("department", v)}>
            <SelectTrigger id="department" data-testid="form-department"><SelectValue placeholder="Select a department" /></SelectTrigger>
            <SelectContent>
              {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">Work email *</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="alice.smith@hospital.org" data-testid="form-email" required />
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-slate-600">Direct phone</Label>
          <Input id="phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(713) 555-0123" data-testid="form-phone" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="current_system" className="text-xs font-bold uppercase tracking-wider text-slate-600">Current water system / vendor</Label>
          <Input id="current_system" value={form.current_system} onChange={(e) => set("current_system", e.target.value)} placeholder="e.g. TRITTON IXC + DI polish, BoilerMag-treated softener, Evoqua service contract..." data-testid="form-current-system" />
          <p className="text-[11px] text-slate-500 mt-1">If you don&rsquo;t know the make/model, just say &ldquo;not sure&rdquo; &mdash; we&rsquo;ll identify it on the visit.</p>
        </div>

        <div className="sm:col-span-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Compliance goals (check all that apply)</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {COMPLIANCE_GOALS.map((g) => (
              <label key={g} className="flex items-start gap-2.5 px-3 py-2 rounded-lg border border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/40 cursor-pointer" data-testid={`form-goal-${g.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}>
                <input
                  type="checkbox"
                  checked={goals.includes(g)}
                  onChange={() => toggleGoal(g)}
                  className="mt-1 w-4 h-4 accent-cyan-600 shrink-0"
                />
                <span className="text-sm text-slate-700 leading-tight">{g}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="comments" className="text-xs font-bold uppercase tracking-wider text-slate-600">Tell us more about your current system</Label>
          <Textarea
            id="comments"
            rows={5}
            value={form.comments}
            onChange={(e) => set("comments", e.target.value)}
            placeholder={
              "What's working, what isn't, what's keeping you up at night? Examples:\n" +
              "• Last Joint Commission survey findings\n" +
              "• Recent water-quality report concerns (high TDS, conductivity drift, endotoxin)\n" +
              "• Capital budget cycle\n" +
              "• Loop / dead-leg issues, leaks, age of equipment, service-contract pain"
            }
            data-testid="form-comments"
          />
        </div>

        <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
          <p className="text-[12px] text-slate-500">
            We use this to schedule the visit only. We never share, sell, or sub-let your contact information.
          </p>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 text-sm font-bold gap-2"
            data-testid="form-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Handshake className="w-4 h-4" />}
            Request my in-service assessment
          </Button>
        </div>
      </form>
    </div>
  );
}


// -----------------------------------------------------------------------------
// DITankExchangeForm — public order form on /solutions/water-treatment
// Submits to POST /api/di-tank-exchange/request
// -----------------------------------------------------------------------------
const TANK_SIZES = [
  "Mixed Bed (ultra-pure polish)",
  "Cation / Anion Dual Bed",
  "1.0 cu ft",
  "2.0 cu ft",
  "3.6 cu ft",
  "7.0 cu ft",
  "Not sure — please advise",
];

function DITankExchangeForm() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    account_number: "",
    email: "",
    phone: "",
    number_of_tanks: "1",
    tank_size: "",
    urgency: "next_day",
    delivery_address: "",
    instructions: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["first_name", "last_name", "company_name", "email", "phone", "number_of_tanks"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill in ${k.replace(/_/g, " ")}`);
        return;
      }
    }
    const tanks = parseInt(form.number_of_tanks, 10);
    if (!tanks || tanks < 1) {
      toast.error("Number of tanks must be at least 1");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/di-tank-exchange/request`, {
        ...form,
        number_of_tanks: tanks,
      });
      setSubmitted(true);
      toast.success("Exchange request received. Dispatch will confirm your window shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Submission failed. Please try again or call 281.456.3458.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center" data-testid="di-form-success">
        <div className="w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Got it &mdash; your DI tank exchange is queued.</h2>
        <p className="text-slate-600 max-w-xl mx-auto mb-4">
          A Sterile Mate dispatcher will reach out shortly to confirm your delivery window. We&rsquo;ve emailed a copy of your request with what to expect on the swap visit.
        </p>
        <p className="text-sm text-slate-500">
          Need to escalate to <strong>same-day emergency service</strong>? Call <a href="tel:2814563458" className="text-cyan-700 font-semibold">281.456.3458</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="di-tank-exchange-form">
      <div className="bg-gradient-to-br from-cyan-700 via-cyan-800 to-slate-900 px-8 lg:px-10 py-7 text-white">
        <p className="text-xs uppercase tracking-widest text-cyan-300 font-bold mb-1">Order a tank exchange</p>
        <h2 className="text-2xl lg:text-3xl font-bold mb-1.5">Schedule your DI Tank swap.</h2>
        <p className="text-slate-200 text-sm max-w-2xl">
          Tell us how many tanks you need and where they&rsquo;re going. Our dispatch team confirms the delivery window the same business day &mdash; <strong className="text-white">same-day service available for emergencies</strong>.
        </p>
      </div>

      <form onSubmit={submit} className="px-8 lg:px-10 py-8 grid sm:grid-cols-2 gap-x-6 gap-y-5">
        <div>
          <Label htmlFor="di_first_name" className="text-xs font-bold uppercase tracking-wider text-slate-600">First name *</Label>
          <Input id="di_first_name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Alice" data-testid="di-form-first-name" required />
        </div>
        <div>
          <Label htmlFor="di_last_name" className="text-xs font-bold uppercase tracking-wider text-slate-600">Last name *</Label>
          <Input id="di_last_name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Smith" data-testid="di-form-last-name" required />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="di_company" className="text-xs font-bold uppercase tracking-wider text-slate-600">Company / Facility name *</Label>
          <Input id="di_company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="e.g. Memorial Hermann Northeast Hospital" data-testid="di-form-company" required />
        </div>
        <div>
          <Label htmlFor="di_account" className="text-xs font-bold uppercase tracking-wider text-slate-600">Account number</Label>
          <Input id="di_account" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="SM-XXXXX (if you have one)" data-testid="di-form-account" />
        </div>
        <div>
          <Label htmlFor="di_email" className="text-xs font-bold uppercase tracking-wider text-slate-600">Email *</Label>
          <Input id="di_email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="alice.smith@hospital.org" data-testid="di-form-email" required />
        </div>
        <div>
          <Label htmlFor="di_phone" className="text-xs font-bold uppercase tracking-wider text-slate-600">Phone *</Label>
          <Input id="di_phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(713) 555-0123" data-testid="di-form-phone" required />
        </div>
        <div>
          <Label htmlFor="di_tanks" className="text-xs font-bold uppercase tracking-wider text-slate-600">Number of tanks *</Label>
          <Input id="di_tanks" type="number" min="1" max="50" value={form.number_of_tanks} onChange={(e) => set("number_of_tanks", e.target.value)} placeholder="1" data-testid="di-form-tanks" required />
        </div>
        <div>
          <Label htmlFor="di_size" className="text-xs font-bold uppercase tracking-wider text-slate-600">Tank size / type</Label>
          <Select value={form.tank_size} onValueChange={(v) => set("tank_size", v)}>
            <SelectTrigger id="di_size" data-testid="di-form-size"><SelectValue placeholder="Select tank size or type" /></SelectTrigger>
            <SelectContent>
              {TANK_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Service level</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className={`flex items-start gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${form.urgency === "next_day" ? "border-cyan-500 bg-cyan-50/60" : "border-slate-200 hover:border-cyan-300"}`} data-testid="di-form-urgency-next-day">
              <input type="radio" name="di_urgency" value="next_day" checked={form.urgency === "next_day"} onChange={(e) => set("urgency", e.target.value)} className="mt-1 accent-cyan-600" />
              <div>
                <p className="text-sm font-bold text-slate-900">Next business day</p>
                <p className="text-xs text-slate-600">Standard exchange. Driver arrives next business day with regenerated, performance-verified tank(s).</p>
              </div>
            </label>
            <label className={`flex items-start gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${form.urgency === "same_day_emergency" ? "border-rose-500 bg-rose-50/60" : "border-slate-200 hover:border-rose-300"}`} data-testid="di-form-urgency-same-day">
              <input type="radio" name="di_urgency" value="same_day_emergency" checked={form.urgency === "same_day_emergency"} onChange={(e) => set("urgency", e.target.value)} className="mt-1 accent-rose-600" />
              <div>
                <p className="text-sm font-bold text-slate-900">Same-day emergency</p>
                <p className="text-xs text-slate-600">Critical exhaustion / patient-care risk. Dispatch will call you within 30 min.</p>
              </div>
            </label>
          </div>
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="di_address" className="text-xs font-bold uppercase tracking-wider text-slate-600">Delivery address (if different from account default)</Label>
          <Input id="di_address" value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} placeholder="Building, dock, suite — anything that helps the driver" data-testid="di-form-address" />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="di_instructions" className="text-xs font-bold uppercase tracking-wider text-slate-600">Any additional instructions for us?</Label>
          <Textarea
            id="di_instructions"
            rows={4}
            value={form.instructions}
            onChange={(e) => set("instructions", e.target.value)}
            placeholder="Loading dock hours, security check-in, on-site contact name, parking notes, gate codes, etc."
            data-testid="di-form-instructions"
          />
        </div>

        <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
          <p className="text-[12px] text-slate-500">
            For same-day urgent escalation, also call <a href="tel:2814563458" className="text-cyan-700 font-semibold">281.456.3458</a>.
          </p>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 text-sm font-bold gap-2"
            data-testid="di-form-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
            Send my exchange request
          </Button>
        </div>
      </form>
    </div>
  );
}
